import type { JSONContent } from '@tiptap/core';
import type { UserAvatarUser } from '#layers/access/types/user-avatar';
import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import UserInvitationNodeView from '#layers/access/components/extensions/user-invitation/UserInvitationNodeView.vue';
import { isValidEmail } from '#layers/access/utils/email';

export interface UserInvitationOptions {
	onPasteEmails?: (emails: string[]) => void;
	isDuplicate?: (email: string) => boolean;
	getUser?: (userId: string) => UserAvatarUser | undefined;
}

export interface UserInvitationAttributes {
	email: string;
	recipientType: 'existing_member' | 'new_email';
	userId?: string | null;
	fullName?: string | null;
}

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		userInvitation: {
			insertInvitation: (attrs: UserInvitationAttributes) => ReturnType;
			removeInvitation: (email: string) => ReturnType;
		};
	}
}

export const UserInvitation = Node.create<UserInvitationOptions>({
	name: 'invitation',
	group: 'inline',
	inline: true,
	atom: true,

	selectable: true,

	addOptions() {
		return {
			onPasteEmails: undefined,
			isDuplicate: () => false,
		};
	},

	addAttributes() {
		return {
			email: {
				default: null,
				parseHTML: (element) => element.dataset.email,
				renderHTML: (attributes) => {
					if (!attributes.email) return;
					return { 'data-email': attributes.email };
				},
			},
			recipientType: {
				default: 'new_email',
				parseHTML: (element) => element.dataset.recipientType,
				renderHTML: (attributes) => {
					return { 'data-recipient-type': attributes.recipientType };
				},
			},
			userId: {
				default: null,
				parseHTML: (element) => element.dataset.userId,
				renderHTML: (attributes) => {
					if (!attributes.userId) return;
					return { 'data-user-id': attributes.userId };
				},
			},
			fullName: {
				default: null,
				parseHTML: (element) => element.dataset.fullName,
				renderHTML: (attributes) => {
					if (!attributes.fullName) return;
					return { 'data-full-name': attributes.fullName };
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ['span', mergeAttributes({ 'data-type': this.name }, HTMLAttributes)];
	},

	addNodeView() {
		return VueNodeViewRenderer(UserInvitationNodeView);
	},

	addCommands() {
		return {
			insertInvitation:
				(attrs) =>
					({ commands }) => {
						return commands.insertContent([
							{ type: this.name, attrs },
							{ type: 'text', text: ' ' },
						]);
					},
			removeInvitation:
				(email) =>
					({ tr, state, dispatch }) => {
						const { doc } = state;
						let found = false;

						doc.descendants((node, pos) => {
							if (node.type.name === this.name && node.attrs.email === email) {
								if (dispatch) {
									tr.delete(pos, pos + node.nodeSize);
								}

								found = true;
								return false;
							}
						});

						return found;
					},
		};
	},

	addProseMirrorPlugins() {
		const options = this.options;

		return [
			new Plugin({
				props: {
					handlePaste: (_view, event) => {
						const text = event.clipboardData?.getData('text') ?? '';
						if (!text) return false;

						const emails = text.split(/[\s,;]+/)
							.map((e) => e.trim().toLowerCase())
							.filter(Boolean)
							.filter((e) => isValidEmail(e))
							.filter((e) => !options.isDuplicate?.(e));

						if (emails.length > 0) {
							event.preventDefault();
							options.onPasteEmails?.(emails);
							return true;
						}

						return false;
					},
				},
			}),
		];
	},
});

export function isUserInvitation(node: JSONContent): node is JSONContent & { attrs: UserInvitationAttributes } {
	return node.type === 'invitation' && !!node.attrs;
}
